import {
    ICreative,
    IExtendedLayout,
    ILayout,
    MimeTypeEnum,
    ProjectRecordingTypeEnum,
    ProjectStatusEnum,
} from '../defines';
import { IBasicEditModel, IEditInDTO, IExportEditJob } from './edit/edit-model';
import { IScene, ISceneInDTO, ISceneOutDTO } from './scene-model';
import { IBranding } from '../user';
import { IProjectIndexData } from 'src/app/services/state-management/project/shared-project-indexdb.service';
import { IDesignGroup } from '../design.model';
import { IProjectInviteModel } from './project-invites-model';
import { AiVoiceOptionIDEnum, IAiVoiceSettings } from '../ai-voice.model';
import { IAiAvatarSettings } from '../ai-avatar-model';
import { IExtraFontSettings } from '../style-font-model';

export interface IBaseConvertor<TInput, ILocal, TOutput> {
    localToOut(client: ILocal, ...args: any[]): TOutput;
}

/**q
 * IInput -> what comes from the server
 * ILocal -> what we transform from the server to do as we wish with him in the client. Do with the bad boy whatever you want
 * IOutput -> what we send back to the server
 */
export interface IConvertor<TInput, ILocal, TOutput> extends IBaseConvertor<TInput, ILocal, TOutput> {
    inToLocal(input: TInput, ...args: any[]): ILocal;
}

export interface IConvertorAsync<TInput, ILocal, TOutput> extends IBaseConvertor<TInput, ILocal, TOutput> {
    inToLocalAsync(input: TInput, ...args: any[]): Promise<ILocal>;
}

export interface IProjectContext {
    projectId: string;
}
export enum ProjectPropertiesToUpdateEnum {
    PROJECT_TITLE = 'name',
    STATUS = 'statuses.general',
    chosenAiVoiceId = 'chosenAiVoiceId',
}

export enum ScenePropertiesToUpdateEnum {
    SCENE_TITLE = 'name',
    COPY = 'copy',
    IS_HIDDEN = 'isHidden',
    SELECTED_TAKE_ID = 'selectedTakeId',
}

export interface IExportUrls {
    exportUrl?: string;
    maxineUrl?: string;
}

export interface IBasicUserInfo {
    email: string;
    firstName: string;
    lastName?: string;
}

export interface IProjectStatuses {
    general: ProjectStatusEnum;
}

export interface ILaunchBranding {
    companyUrl: string;
    logo: string;
    colors: {
        colorPrimary?: string;
        colorSecondary?: string;
        colorExtra?: string;
    };
}

export interface ILaunchAnswers {
    name: string;
    email: string;
    question: string;
    shortQuestion: string;
    answer: string;
    branding: ILaunchBranding;
    projectCode: string;
}

export interface IBasicProjectData {
    id: string;
    name: string;
    user: IBasicUserInfo;
    title: string;
    designGroup: IDesignGroup;
    toMaxine: boolean;
    updatedAt: Date;
    statuses: IProjectStatuses;
    creative: ICreative;
    icon: string;
    createdAt: number;
    edits: IBasicEditModel[];
    launchAnswers: ILaunchAnswers;
    branding: IBranding;
    invites: IProjectInviteModel[];
    isProjectOwner?: boolean;
    videoType: ProjectRecordingTypeEnum;
    aiVoiceSettings: IAiVoiceSettings;
    aiAvatarSettings: IAiAvatarSettings;
    chosenAiVoiceId?: AiVoiceOptionIDEnum;
    fonts: IExtraFontSettings[];
    shortTitle?: string;
    isPlayground: boolean;
    language: string;
}

export interface IProjectInDTO extends IBasicProjectData {
    scenes: ISceneInDTO[];
    edits: IEditInDTO[];
}

/// Doing project configs to make other properties / functions inside the constructor of IProject
export interface IProjectConfigs extends IBasicProjectData {
    scenes: IScene[];
    edits: IExportEditJob[];
    indexDBData: IProjectIndexData;
    isProjectOwner: boolean;
    formattedTime: string;
    typeIcon: string;
    tooltip: string;
    badgeLabel: string;
    exportsCount: number;
    showExportCount: boolean;
}
export interface IProject extends IProjectConfigs {}

export class Project implements IProject {
    id: string;
    name: string;
    user: IBasicUserInfo;
    title: string;
    designGroup: IDesignGroup;
    toMaxine: boolean;
    updatedAt: Date;
    statuses: IProjectStatuses;
    creative: ICreative;
    icon: string;
    createdAt: number;
    scenes: IScene[];
    edits: IExportEditJob[];
    indexDBData: IProjectIndexData;
    launchAnswers: ILaunchAnswers;
    branding: IBranding;
    isProjectOwner: boolean;

    invites: IProjectInviteModel[];
    videoType: ProjectRecordingTypeEnum;
    shortTitle: string;
    aiVoiceSettings: IAiVoiceSettings;
    aiAvatarSettings: IAiAvatarSettings;
    isPlayground: boolean;
    language: string;
    fonts: IExtraFontSettings[];
    constructor(projectConfigs: IProjectConfigs) {
        Object.assign(this, projectConfigs);
    }

    badgeLabel: string;
    exportsCount: number;
    formattedTime: string;
    showExportCount: boolean;
    tooltip: string;
    typeIcon: string;
}

export interface IProjectOutDTO extends IBasicProjectData {
    scenes: ISceneOutDTO[];
}
export interface IExtendedProjectInDTO extends IProjectInDTO {
    iconLayout?: IExtendedLayout;
    formattedTime: string;
    typeIcon: string;
    tooltip: string;
    badgeLabel: string;
    exportsCount: number;
    showExportCount: boolean;
}
